// ============================================================================
// OBT F3 — Nihai karar gerekçe diyaloğu (§17)
// ============================================================================
// Bir aday park için karar (dahil et / çıkar / ertele) gerekçesini toplar.
// Ürün kuralı: ÇIKARMA ve ERTELEME için gerekçe ZORUNLU; DAHİL ETME için
// opsiyonel. Backend'de reason-code YOKTUR — öneri çipleri yalnız serbest
// açıklama alanını doldurur (yeni enum uydurulmaz).
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { DECISION_ITEM_LABELS, DECISION_VALUE } from './reviewEnums';
import {
  MAX_DECISION_TEXT_LEN,
  isDecisionExplanationRequired,
  isDecisionItemValid,
} from './reviewValidation';

// Serbest açıklamayı hızlı doldurmak için öneri metinleri (backend reason-code
// DEĞİL; yalnız kolaylık).
const REASON_SUGGESTIONS = Object.freeze([
  'Bütçe önceliği',
  'Teknik uygunluk',
  'Saha ihtiyacı',
  'Mükerrer kapsam',
  'Yönetim kararı',
]);

/**
 * @param {object} props
 * @param {{responseId: string, decision: string, parkName?: string, explanation?: string}|null} props.pending
 * @param {() => void} props.onClose
 * @param {(explanation: string) => Promise<void>} props.onConfirm
 * @param {string|null} [props.error]
 */
export default function FinalDecisionDialog({ pending, onClose, onConfirm, error = null }) {
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const open = Boolean(pending);
  const decision = pending?.decision;

  useEffect(() => {
    if (open) {
      setExplanation(pending?.explanation || '');
      setSubmitting(false);
    }
  }, [open, pending]);

  const required = isDecisionExplanationRequired(decision);
  const valid = isDecisionItemValid(decision, explanation);

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(explanation.trim());
    } catch {
      setSubmitting(false);
    }
  };

  const decisionLabel = decision ? DECISION_ITEM_LABELS[decision] : '';

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Karar Gerekçesi</DialogTitle>
          <DialogDescription>
            {`"${pending?.parkName || 'Bu park'}" için karar: `}
            <span className="font-semibold">{decisionLabel}</span>.
            {decision === DECISION_VALUE.SELECTED
              ? ' Gerekçe isteğe bağlıdır.'
              : ' Gerekçe zorunludur.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {REASON_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setExplanation(s)}
                disabled={submitting}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-200"
              >
                {s}
              </button>
            ))}
          </div>
          <Label htmlFor="decision-explanation">
            {required ? 'Gerekçe *' : 'Gerekçe (opsiyonel)'}
          </Label>
          <Textarea
            id="decision-explanation"
            value={explanation}
            maxLength={MAX_DECISION_TEXT_LEN}
            rows={3}
            placeholder="Kararın gerekçesini yazın…"
            onChange={(e) => setExplanation(e.target.value)}
            disabled={submitting}
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Vazgeç
          </Button>
          <Button onClick={handleConfirm} disabled={!valid || submitting}>
            {submitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            Kararı Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
